// ── Exam coding task workspace (EX-EXM02) ───────────────────────────────────
// Client for the four student-facing routes `EX-EXM01` shipped —
// `docs/07_API_CONTRACT.md` "Exam coding tasks", `docs/27_JUDGE_AND_EXAM_SPEC.md`
// §9.7. Its own file (not `api.ts`) because it is a distinct sub-surface with its
// own response shapes, the same split `subscription.ts` already uses.

import { fetcher } from "@/lib/api";
import { laneRoute, runThroughLane } from "@/features/judge/lib/run-lane";
import type { CadenceSnapshot, PlaybackBatch } from "@/features/proctor";
import type { ProblemFileIo } from "@/features/judge/lib/file-io";
import type { CodingTaskDraft, CodingTaskFeedbackPolicy } from "./types";

/** `docs/27` §7.1 — the stable branching key for a graded verdict. */
export const JUDGE_VERDICTS = [
  "accepted",
  "wrong_answer",
  "time_limit_exceeded",
  "memory_limit_exceeded",
  "runtime_error",
  "compilation_error",
  "presentation_error",
  "internal_error",
  "skipped",
] as const;
export type JudgeVerdict = (typeof JUDGE_VERDICTS)[number];

/** A sample run's per-case outcome — a different, smaller taxonomy than {@link JudgeVerdict}. */
export type SampleRunOutcome =
  | "completed"
  | "timed_out"
  | "memory_exceeded"
  | "crashed"
  | "internal_error";

/** Declared in `types.ts` beside the creator-side config, re-exported for this surface's callers. */
export type { CodingTaskFeedbackPolicy };

/** `JudgeSubmission.sourceCode`'s ceiling (`docs/27` §9.7.4) — a draft that autosaves is a draft that can be submitted. */
export const CODING_SOURCE_CODE_MAX = 50_000;

export type CodingTaskRuntime = {
  languageKey: string;
  languageVersion: string;
};

export type CodingTaskSample = {
  idx: number;
  input: string;
  output: string;
  explanation_en?: string;
  explanation_bn?: string;
};

export type CodingTaskProblemView = {
  title_en: string;
  title_bn?: string;
  statement_en: string;
  statement_bn?: string;
  inputFormat_en?: string;
  inputFormat_bn?: string;
  outputFormat_en?: string;
  outputFormat_bn?: string;
  constraints_en?: string;
  constraints_bn?: string;
  notes_en?: string;
  notes_bn?: string;
  samples: CodingTaskSample[];
  limits: { timeMs: number; memoryKb: number };
  /** `EX-RTW05`: present only when the program reads or writes a file. */
  fileIo?: ProblemFileIo;
};

/** GET /v1/quiz-sessions/:sessionId/coding/:questionId */
export type CodingTaskDetail = {
  questionId: string;
  marks: number;
  problem: CodingTaskProblemView;
  allowedRuntimes: CodingTaskRuntime[];
  feedbackPolicy: CodingTaskFeedbackPolicy;
  submitLimit: number;
  submitsUsed: number;
  submitsRemaining: number;
  penaltyPerWrongSubmission: number;
  draft: CodingTaskDraft | null;
};

export type SampleRunCaseResult = {
  idx: number;
  stdout: string;
  stderr: string;
  timeMs: number | null;
  memoryKb: number | null;
  outputTruncated: boolean;
  outcome: SampleRunOutcome;
  /** `null` when nothing honestly compared — a custom-checker problem, custom stdin, or an incomplete run. */
  passed: boolean | null;
  /**
   * `EX-RTW03`: a run-twice problem only. Where the case stopped and each
   * stage as it ran; the fields above describe the last run that executed.
   */
  stage?: RunTwiceStage;
  stages?: SampleRunStages;
  /**
   * `EX-IAC01`: an interactive problem only. The interactor's verdict on the
   * case and the conversation; `stdout` above is the program's side of it.
   */
  interaction?: SampleRunInteraction;
};

/** `EX-IAC01`: one side's words in a conversation with the interactor. */
export type TranscriptEntry = { from: "solution" | "interactor"; text: string };

export type SampleRunInteraction = {
  verdict: JudgeVerdict;
  /** The program was waiting on the interactor when its time ran out. */
  idle?: true;
  transcript: TranscriptEntry[];
  transcriptTruncated: boolean;
  /** The interactor's own comment, sent only to the problem's team. */
  juryComment?: string;
};

/** `EX-RTW01`: where a run-twice test stopped: run 1, the jury's check, or run 2. */
export type RunTwiceStage = "first" | "bridge" | "second";

/** One box run of a run-twice sample, as the run panel shows it. */
export type SampleRunStageRun = {
  stdout: string;
  stderr: string;
  timeMs: number | null;
  memoryKb: number | null;
  outputTruncated: boolean;
  outcome: SampleRunOutcome;
};

/**
 * `EX-RTW01`: the three stages of one run-twice case. The bridge is absent
 * when run 1 did not finish; run 2's input is built from input the reader can
 * already see (a public sample or their own), so it is shown.
 */
export type SampleRunStages = {
  first: SampleRunStageRun | null;
  bridge?: { status: "ok" | "invalid" | "error"; secondInput?: string; reason?: string };
  second?: SampleRunStageRun;
  /** `EX-IAC01`: with the interactor bridge, each run's conversation with the interactor. */
  firstTranscript?: TranscriptEntry[];
  secondTranscript?: TranscriptEntry[];
};

/** POST /v1/quiz-sessions/:sessionId/coding/:questionId/run */
export type CodingTaskRunResult = {
  compiled: boolean;
  compileOutput: string | null;
  results: SampleRunCaseResult[];
  customInput: boolean;
};

/** POST /v1/quiz-sessions/:sessionId/coding/:questionId/submit (202) */
export type CodingTaskSubmitResult = {
  submissionId: string;
  status: "queued" | "running" | "completed";
  queuePosition: number;
  submitsUsed: number;
  submitsRemaining: number;
};

export type StudentTestResult = {
  idx: number;
  verdict: JudgeVerdict;
  points: number;
  /** `EX-RTW04`: the share of the test's points a partial-points checker gave, below full. */
  share?: number;
  timeMs?: number;
  memoryKb?: number;
  /** `EX-RTW01`: a run-twice row only, where the test stopped and each run's figures. */
  stage?: RunTwiceStage;
  runs?: { timeMs?: number; memoryKb?: number }[];
  /** `EX-IAC01`: on an interactive time-limit row, the program was waiting, not working. */
  idle?: true;
};

/**
 * `EX-RTW03`: a run-twice submission summed up where no test is named (a
 * contest's `count_only`): where the first failing test stopped, and each
 * run's largest time and memory.
 */
export type RunTwiceSummary = {
  stage?: RunTwiceStage;
  runs: { timeMs?: number; memoryKb?: number }[];
};

/** A submission shaped by the task's `feedbackPolicy` — never the raw `JudgeSubmission`. */
export type CodingSubmissionView = {
  status: "queued" | "running" | "completed";
  verdict?: JudgeVerdict;
  score: number;
  maxScore: number;
  timeMs?: number;
  memoryKb?: number;
  compileOutput?: string;
  /** Absent under `count_only` and `none`. */
  testResults?: StudentTestResult[];
  /** Present whenever some results are withheld. */
  hidden?: { passedCount: number; totalCount: number };
};

export type CodingTaskSubmission = {
  submissionId: string;
  createdAt: string;
  result: CodingSubmissionView;
};

/** GET /v1/quiz-sessions/:sessionId/coding/:questionId/submissions */
export type CodingTaskSubmissionsResult = {
  submissions: CodingTaskSubmission[];
};

export type CodingTaskRunInput = {
  languageKey: string;
  languageVersion: string;
  sourceCode: string;
  /** When present, one run against this instead of the samples. */
  stdin?: string;
};

export type CodingTaskSubmitInput = {
  languageKey: string;
  languageVersion: string;
  sourceCode: string;
  /**
   * Typing cadence — `EX-AUT01`, `docs/27` §9.17.
   *
   * A shape and nothing else: window offsets and keystroke counts, plus how long
   * the recorder ran. No pasted text, no key, no timestamp, and no total the
   * server can compute for itself — the route's schema is `.strict()` and would
   * answer `400` to any of them. The server re-buckets, clamps and re-anchors it
   * before storage, so this is a report and never a record.
   */
  cadence?: CadenceSnapshot;
};

/** A submission still awaiting a verdict — drives the workspace's poll. */
export function isCodingSubmissionPending(result: CodingSubmissionView): boolean {
  return result.status !== "completed";
}

export async function fetchCodingTask(
  sessionId: string,
  questionId: string,
): Promise<CodingTaskDetail> {
  const res = await fetcher<CodingTaskDetail>(
    `/v1/quiz-sessions/${sessionId}/coding/${questionId}`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** Feedback only — no submission row, no submit slot. */
export async function runCodingTask(
  sessionId: string,
  questionId: string,
  input: CodingTaskRunInput,
): Promise<CodingTaskRunResult> {
  // `EX-SRN01`: waits its turn for a share of the judge rather than being refused.
  return runThroughLane(async () => {
    const res = await fetcher<CodingTaskRunResult>(
      laneRoute(`/v1/quiz-sessions/${sessionId}/coding/${questionId}/run`),
      { method: "POST", body: JSON.stringify(input) },
    );
    if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
    return res.data;
  });
}

/** Enqueues a graded submission in the `exam` band — costs a submit slot. */
export async function submitCodingTask(
  sessionId: string,
  questionId: string,
  input: CodingTaskSubmitInput,
): Promise<CodingTaskSubmitResult> {
  const res = await fetcher<CodingTaskSubmitResult>(
    `/v1/quiz-sessions/${sessionId}/coding/${questionId}/submit`,
    { method: "POST", body: JSON.stringify(input) },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

/** POST /v1/quiz-sessions/:sessionId/coding/:questionId/playback (`EX-AUT02`) */
export type CodePlaybackAck = {
  accepted: number;
  rejected: number;
  /** What the server now holds — the `base` the next batch declares. */
  frameCount: number;
  /** The recording hit a bound and is closed. The recorder stops offering frames. */
  truncated: boolean;
};

/**
 * Append one flush of the editor's history — `EX-AUT02`, `docs/27` §9.18.
 *
 * The response carries counters and never a frame: the server has nothing to
 * tell a student about their own recording except how much of the last offer it
 * took and how much it now holds, which is all the recorder needs in order to
 * decide between continuing the chain and re-keying it.
 */
export async function sendCodePlayback(
  sessionId: string,
  questionId: string,
  batch: PlaybackBatch,
): Promise<CodePlaybackAck> {
  const res = await fetcher<CodePlaybackAck>(
    `/v1/quiz-sessions/${sessionId}/coding/${questionId}/playback`,
    { method: "POST", body: JSON.stringify(batch) },
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}

export async function fetchCodingSubmissions(
  sessionId: string,
  questionId: string,
): Promise<CodingTaskSubmissionsResult> {
  const res = await fetcher<CodingTaskSubmissionsResult>(
    `/v1/quiz-sessions/${sessionId}/coding/${questionId}/submissions`,
  );
  if (!res.success) throw new Error((res as { error?: { message?: string } }).error?.message);
  return res.data;
}
